import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorState } from './ErrorState';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RÜCKFRAGEDIALOG (Auftrag 17.09.2026, Abschnitt 16; Abschnitt 28/29)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Dialoge standardisieren: Bestätigung, Bearbeiten, Löschen, Import,
 *    Export, Berechnung starten."
 *
 * Für WENIGE Angaben und eine Entscheidung. Alles Umfangreichere gehört ins
 * `Drawer` - so steht es im Auftrag, und in dieser Anwendung ist der Fall
 * häufig (dreißig Kennwerte passen in kein Modal).
 *
 * VIER ARTEN, eine Farbe je Art:
 *   `standard`  neutral, z. B. „Berechnung starten"
 *   `warnung`   Folgen, die Arbeit kosten, z. B. „Aufnahme wiederholen"
 *   `loeschen`  unwiederbringlich - der Bestätigungsknopf ist rot
 *   `hinweis`   reine Kenntnisnahme, nur ein Knopf
 *
 * Der Dialog schließt NICHT von selbst, wenn `laeuft` gesetzt ist: Ein
 * laufender Serverauftrag soll nicht hinter einem verschwundenen Fenster
 * weiterlaufen. Beim Scheitern nimmt `fehler` den Wortlaut auf.
 */

export type DialogArt = 'standard' | 'warnung' | 'loeschen' | 'hinweis';

export interface ConfirmDialogProps {
  offen: boolean;
  titel: string;
  /** Was geschieht und was das bedeutet - in Alltagssprache. */
  text?: ReactNode;
  /** Zusätzliche Felder (wenige!), z. B. eine Auswahl vor dem Start. */
  children?: ReactNode;
  art?: DialogArt;
  bestaetigenText?: string;
  abbrechenText?: string;
  beiBestaetigen: () => void;
  beiAbbrechen: () => void;
  /** Sperrt die Knöpfe und zeigt, dass der Auftrag läuft. */
  laeuft?: boolean;
  /** Fehler des letzten Versuchs; bleibt im Dialog stehen. */
  fehler?: unknown;
  /** Verlangt, dass der Anwender ein Wort tippt (nur bei `loeschen` sinnvoll). */
  bestaetigungswort?: string;
  className?: string;
}

export function ConfirmDialog({
  offen, titel, text, children, art = 'standard',
  bestaetigenText, abbrechenText = 'Abbrechen',
  beiBestaetigen, beiAbbrechen, laeuft, fehler, bestaetigungswort, className,
}: ConfirmDialogProps) {
  const flaeche = useRef<HTMLDivElement | null>(null);
  const vorher = useRef<HTMLElement | null>(null);
  const titelId = useId();
  const textId = useId();
  const wortId = useId();
  const [getippt, setGetippt] = useState('');

  useEffect(() => {
    // Das getippte Bestätigungswort gilt nur für dieses eine Öffnen.
    if (!offen) setGetippt('');
  }, [offen]);

  useEffect(() => {
    if (!offen) return undefined;
    vorher.current = document.activeElement as HTMLElement | null;
    const id = window.setTimeout(() => {
      // Der Fokus geht auf den ABBRECHEN-Knopf, nicht auf „Löschen" -
      // ein versehentliches Enter darf nichts vernichten.
      const ziel = flaeche.current?.querySelector<HTMLElement>('[data-erstfokus]')
        ?? flaeche.current;
      ziel?.focus();
    }, 0);
    return () => {
      window.clearTimeout(id);
      vorher.current?.focus?.();
    };
  }, [offen]);

  if (!offen) return null;

  const bestaetigen = bestaetigenText ?? (art === 'loeschen' ? 'Löschen' : 'Bestätigen');

  function beiTaste(ereignis: React.KeyboardEvent) {
    if (ereignis.key === 'Escape' && !laeuft) {
      ereignis.preventDefault();
      beiAbbrechen();
    }
  }

  return (
    <div
      className="m-dialog-hintergrund"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !laeuft) beiAbbrechen();
      }}
    >
      <div
        className={`m-dialog m-dialog-${art}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        aria-describedby={text ? textId : undefined}
        ref={flaeche}
        tabIndex={-1}
        onKeyDown={beiTaste}
      >
        <h2 className="m-dialog-titel" id={titelId}>
          {art !== 'standard' && (
            <span className="m-dialog-zeichen" aria-hidden="true">
              {art === 'loeschen' ? '×' : art === 'warnung' ? '!' : 'i'}
            </span>
          )}
          {titel}
        </h2>

        {text && <div className="m-dialog-text" id={textId}>{text}</div>}
        {children && <div className="m-dialog-felder">{children}</div>}

        {bestaetigungswort && (
          <div className="m-dialog-wort">
            <label htmlFor={wortId}>
              Zum Bestätigen „{bestaetigungswort}" eingeben:
            </label>
            <input
              id={wortId}
              type="text"
              autoComplete="off"
              value={getippt}
              onChange={(e) => setGetippt(e.target.value)}
            />
          </div>
        )}

        {fehler !== undefined && fehler !== null && (
          <ErrorState
            titel="Der Vorgang ist gescheitert."
            technisch={fehler}
            klein
          />
        )}

        <div className="m-dialog-knoepfe">
          {art !== 'hinweis' && (
            <button
              type="button"
              className="m-knopf"
              data-erstfokus
              disabled={laeuft}
              onClick={beiAbbrechen}
            >
              {abbrechenText}
            </button>
          )}
          <button
            type="button"
            className={`m-knopf ${art === 'loeschen' ? 'm-knopf-gefahr' : 'm-knopf-haupt'}`}
            disabled={laeuft || (!!bestaetigungswort
              && getippt.trim().toLowerCase() !== bestaetigungswort.trim().toLowerCase())}
            data-erstfokus={art === 'hinweis' ? true : undefined}
            onClick={beiBestaetigen}
          >
            {laeuft ? 'läuft …' : bestaetigen}
          </button>
        </div>
      </div>
    </div>
  );
}
